import React, { Fragment, useRef } from "react";
import { createPortal } from "react-dom";
import { Menu, Transition } from "@headlessui/react";
import { HiOutlineChevronDown } from "react-icons/hi2";
import { classNames } from "Utils/utils";
import { useAnchoredMenu } from "hooks/useAnchoredMenu";
import "./ThemedDropdown.css";

function AnchoredMenuItems({ open, anchorRef, children }) {
  const style = useAnchoredMenu({ open, anchorRef });
  if (!open || !style) return null;
  const root = document.querySelector("[data-bulk-analyse-menu-root]") || document.body;
  return createPortal(
    <Menu.Items
      static
      style={style}
      className="themed-dropdown-menu pointer-events-auto flex flex-col overflow-hidden rounded-xl border border-[#E5E7EB] bg-white py-1 focus:outline-none"
    >
      <div className="themed-dropdown-scroll custom-scroll min-h-0" style={{ maxHeight: style.maxHeight }}>
        {children}
      </div>
    </Menu.Items>,
    root
  );
}

/**
 * Compact 34px filter pill.
 * Single: onChange(value, option)
 * Multiple: onChange(values) — empty array means all.
 */
const ThemedDropdown = ({
  label,
  value,
  options = [],
  onChange,
  widthClass = "w-full",
  textClass = "text-[11px] font-medium",
  leadingIcon = null,
  applied = false,
  disabled = false,
  placeholder = "Select",
  multiple = false,
  allLabel = "All",
  anchored = false,
}) => {
  const buttonRef = useRef(null);
  const selectedValues = multiple
    ? (Array.isArray(value) ? value.filter((item) => item && item !== "all") : [])
    : [];
  const selected = multiple
    ? null
    : options.find((option) => option.value === value);
  const firstSelected = options.find((option) => option.value === selectedValues[0]);
  const displayLabel = multiple
    ? selectedValues.length === 0
      ? allLabel
      : selectedValues.length === 1
        ? firstSelected?.label || selectedValues[0]
        : `${firstSelected?.label || "Categories"} +${selectedValues.length - 1}`
    : selected?.label || placeholder;

  const toggleValue = (optionValue) => {
    if (!multiple) {
      onChange?.(optionValue, options.find((option) => option.value === optionValue));
      return;
    }
    if (optionValue === "all") {
      onChange?.([]);
      return;
    }
    const next = selectedValues.includes(optionValue)
      ? selectedValues.filter((item) => item !== optionValue)
      : [...selectedValues, optionValue];
    onChange?.(next);
  };

  const menuOptions = (
    multiple
      ? [{ value: "all", label: allLabel }, ...options.filter((option) => option.value !== "all")]
      : options
  ).map((option) => {
    const isSelected = multiple
      ? option.value === "all"
        ? selectedValues.length === 0
        : selectedValues.includes(option.value)
      : option.value === value;
    const itemClass = classNames(
      "flex w-full items-center gap-2 whitespace-nowrap px-3 py-2.5 text-left text-[13px]",
      isSelected
        ? "bg-[#EEF2FB] font-semibold text-[#4A6BB6]"
        : "font-medium text-[#0F172A] hover:bg-[#F8FAFC]"
    );
    if (multiple) {
      return (
        <button
          key={String(option.value)}
          type="button"
          aria-pressed={isSelected}
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => toggleValue(option.value)}
          className={itemClass}
        >
          <span
            className={classNames(
              "flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded border text-[10px] leading-none",
              isSelected
                ? "border-[#4A6BB6] bg-[#4A6BB6] text-white"
                : "border-[#CBD5E1] bg-white text-transparent"
            )}
            aria-hidden
          >
            ✓
          </span>
          {option.label}
        </button>
      );
    }
    return (
      <Menu.Item key={String(option.value)}>
        {({ active: itemActive }) => (
          <button
            type="button"
            onClick={() => toggleValue(option.value)}
            className={classNames(itemClass, !isSelected && itemActive && "bg-[#F8FAFC]")}
          >
            {option.label}
          </button>
        )}
      </Menu.Item>
    );
  });

  return (
    <div className={classNames("min-w-0", widthClass)}>
      {label && (
        <p className="mb-1.5 text-[10px] font-semibold uppercase leading-none tracking-[0.6px] text-[#94A3B8]">
          {label}
        </p>
      )}
      <Menu as="div" className="relative w-full">
        {({ open }) => (
          <>
            <Menu.Button
              ref={buttonRef}
              disabled={disabled}
              className={classNames(
                "themed-dropdown-trigger flex w-full items-center gap-2 px-[11px] text-left leading-none text-[#0F172A]",
                textClass,
                open && "is-open",
                applied && "is-applied",
                disabled && "cursor-not-allowed opacity-50"
              )}
            >
              {leadingIcon}
              <span className="min-w-0 flex-1 truncate">{displayLabel}</span>
              <HiOutlineChevronDown
                className={classNames(
                  "h-3.5 w-3.5 shrink-0 text-[#94A3B8] transition-transform duration-150",
                  open && "rotate-180"
                )}
                aria-hidden
              />
            </Menu.Button>
            {anchored ? (
              <AnchoredMenuItems open={open} anchorRef={buttonRef}>
                {menuOptions}
              </AnchoredMenuItems>
            ) : null}
            {!anchored ? (
              <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <Menu.Items className="themed-dropdown-menu absolute z-[10020] mt-1 w-max min-w-full overflow-hidden rounded-xl border border-[#E5E7EB] bg-white py-1 focus:outline-none">
                  <div className="themed-dropdown-scroll custom-scroll">
                    {menuOptions}
                  </div>
                </Menu.Items>
              </Transition>
            ) : null}
          </>
        )}
      </Menu>
    </div>
  );
};

export default ThemedDropdown;
